import { Fragment, useState } from 'react';

import clsx from 'clsx';

import Accordion from 'components/Accordion';
import Button from 'components/Button';
import Drawer from 'components/Drawer';
import Icon from 'components/Icon';
import Typography from 'components/Typography';
import { useUserAuthDialog } from 'components/UserAuthDialog';
import { PROFILE_LINKS } from 'components/UserSignedInButton';
import { useEventContext } from 'context/EventContext';
import detachTask from 'helpers/detachTask';
import { ROUTE_NAMES } from 'routes';
import routeService from 'services/routes';

import Header from './components/Header/Header';
import IconLink from './components/IconLink';
import { popularCategories } from './constants';

import classes from './UserDrawer.module.scss';

/** @type {import('./types').default} */
const UserDrawer = ({ user, className }) => {
  const { first_name: name, picture_url: pictureUrl } = user || {};
  const [isOpen, setState] = useState(false);
  const isLoggedIn = !!user;
  const [UserAuthDialog, openUserAuthDialog] = useUserAuthDialog({
    source: 'drawer',
  });

  const { addEvent, sendEvent } = useEventContext();

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    if (open) {
      detachTask(sendEvent, {
        engagementComponent: 'hamburger-menu',
        engagementDestination: 'open-hamburger-menu',
        engagementDetails: 'icon_hamburger',
        name: 'engagement_navigation',
      });
    }

    detachTask(setState, open);
  };

  const accordionOnExpandClick = (event, { isExpanded }) => {
    detachTask(sendEvent, {
      engagementComponent: 'user-drawer',
      engagementDestination: isExpanded ? 'expand-card' : 'collapse-card',
      engagementDetails: 'expansion-panel_popular-categories',
      name: 'engagement_navigation',
    });
  };

  return (
    <>
      <Icon
        aria-hidden="false"
        className={clsx(classes.button, className)}
        data-testid="user_drawer-button"
        icon="bars"
        size={16}
        role="button"
        onClick={toggleDrawer(true)}
      />
      <Drawer
        className={classes.paper}
        data-testid="user_drawer"
        onClose={toggleDrawer(false)}
        open={isOpen}
      >
        <Header isLoggedIn={isLoggedIn} name={name} pictureUrl={pictureUrl} />
        <div
          className={clsx(
            classes.profileInfo,
            'isLoggedIn',
            isLoggedIn && classes.profileLinks
          )}
        >
          {isLoggedIn ? (
            Object.values(PROFILE_LINKS).map((link, index, links) => (
              <Fragment key={link.label}>
                {index > 0 && link.group !== links[index - 1].group && (
                  <div aria-hidden="true" className={classes.divider} />
                )}
                <IconLink link={link} />
              </Fragment>
            ))
          ) : (
            <>
              <Typography
                className={classes.profileInfoText}
                variant="tight-xxs"
              >
                Sign in to save apps, view favorites, and more.
              </Typography>
              <div className={classes.profileInfoButtons}>
                <Button
                  variant="outlined"
                  className={clsx(
                    classes.profileInfoButton,
                    classes.authButton
                  )}
                  onClick={() =>
                    openUserAuthDialog({ selectedVariant: 'join' })
                  }
                  {...addEvent({
                    engagementComponent: 'user-drawer',
                    engagementDestination: 'go-to_join-or-login',
                    engagementDetails: 'button_join-or-login',
                    name: 'login_cta',
                  })}
                >
                  <Typography variant="button-md">Join or Log in</Typography>
                </Button>
              </div>
            </>
          )}
        </div>
        <Accordion
          className={classes.accordion}
          summary={
            <Typography variant="heading-sm">Popular categories</Typography>
          }
          details={
            <>
              {popularCategories.map((category) => (
                <Typography
                  className={classes.popularCategoryLink}
                  component="a"
                  href={category.href}
                  key={category.slug}
                  variant="body-sm"
                  {...addEvent({
                    engagementComponent: 'user-drawer',
                    engagementDestination: 'go-to_category_all-products',
                    engagementDetails: 'list-item_category-name',
                    name: 'engagement_navigation',
                  })}
                >
                  {category.name}
                </Typography>
              ))}
            </>
          }
          detailsClassname={classes.accordionDetails}
          onExpandClick={accordionOnExpandClick}
          summaryClassname={classes.accordionSummary}
        />
        <div className={classes.allCategoriesLinkContainer}>
          <a
            {...addEvent({
              engagementComponent: 'user-drawer',
              engagementDestination: 'go-to_software-categories',
              engagementDetails: 'button_browse',
              name: 'engagement_navigation',
            })}
            className={classes.siteMenuLink}
            href="/browse/"
            variant="heading-sm"
          >
            Software categories
          </a>
          <a
            {...addEvent({
              engagementComponent: 'user-drawer',
              engagementDestination: 'go-to_resources',
              engagementDetails: 'button_resources',
              name: 'engagement_navigation',
            })}
            className={classes.siteMenuLink}
            href="/resources/"
            variant="heading-sm"
          >
            Blog & research
          </a>
          <a
            {...addEvent({
              engagementComponent: 'user-drawer',
              engagementDestination: 'go-to_about',
              engagementDetails: 'button_about',
              name: 'engagement_navigation',
            })}
            className={classes.siteMenuLink}
            href={routeService.generatePath(ROUTE_NAMES.ABOUT)}
            variant="heading-sm"
          >
            About us
          </a>
        </div>
      </Drawer>
      <UserAuthDialog />
    </>
  );
};

export default UserDrawer;
